import { Card, CardContent } from "../components/ui/card";
import { Menu } from "../components/ui/menu";
import Impersonate from "../components/ui/impersonate";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Markdown, { Components } from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter"
import { dark } from "react-syntax-highlighter/dist/esm/styles/prism"
import rehypeKatex from 'rehype-katex'
import remarkMath from 'remark-math'
import remarkGfm from 'remark-gfm'
import 'katex/dist/katex.min.css'
import { API } from "../config";
import { useProfile } from "../context/ProfileContext";
import { Button } from "../components/ui/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faFloppyDisk, faPencil, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import ErrorBoundary from "./ErrorBoundary"; // `rehype-katex` does not import the CSS for you

type MarkdownH1Props = Components['h1'];
type Post = {id: string; title?: string; date?: string; body: string, enable?: boolean};
type PostsById = Record<string, Post>; // or Record<number, Post> if ids are numbers

export const MarkdownHeading1: Components['h1'] = ({ node, className, children, ...restProps }) => (
  <ErrorBoundary name={"MarkdownHeading1"} key={"markdown-heading-1"}>
    <h1
      className={`text-2xl font-bold my-4 ${className || ''}`}
      {...restProps}
    >
      {children}
    </h1>
  </ErrorBoundary>
);

export const MarkdownHeading2: Components['h2'] = ({ node, className, children, ...restProps }) => (
  <ErrorBoundary name={"MarkdownHeading2"} key={"markdown-heading-2"}>
    <h2
      className={`text-2xl font-bold my-4 ${className || ''}`}
      {...restProps}
    >
      {children}
    </h2>
  </ErrorBoundary>
);

export const MarkdownHeading3: Components['h3'] = ({ node, className, children, ...restProps }) => (
  <ErrorBoundary name={"MarkdownHeading3"} key={"markdown-heading-3"}>
    <h3
      className={`font-bold my-4 ${className || ''}`}
      {...restProps}
    >
      {children}
    </h3>
  </ErrorBoundary>
);

export const MarkdownA: Components['a'] = ({ node, className, children, ...restProps }) => {
  const regex = /^(http|https):\/\/blank%25/;
  const target = String(restProps?.href || "").match(regex) ? "_blank" : undefined;
  restProps.href = String(restProps?.href || "").replace(regex, "$1://");
  children = String(children || "").replace(/^(http|https):\/\/blank%/, "$1://");
  return (
    <ErrorBoundary name={"MarkdownA"} key={"markdown-a"}>
      <a
        className={`text-blue-500 font-bold my-4 ${className || ''}`}
        {...restProps}
        target={target}
      >
        {children}
      </a>
    </ErrorBoundary>
  )
};

type MarkdownProps = {
  group: string;
  message: string;
  isEditable?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

const ShowMessage = (postId: string, post: Post) => (
  <ErrorBoundary name={"MarkdownPage:ShowMessage-" + postId} key={"markdown-show-message-" + postId}>
    <div className={post?.enable === false ? "opacity-20" : ""}>
      <h1
        className="text-2xl font-semibold mb-6 text-center">{post?.title || ""}{post?.date ? `(${post?.date})` : ""}</h1>

      <div className="">

        <div className="overflow-auto">
          <ErrorBoundary name={"MarkdownPage:ShowMessage:MarkdownContent-" + postId}
                         key={"markdown-show-message-markdowncontent-" + postId}>
            <Markdown
              remarkPlugins={[remarkGfm, remarkMath]}
              rehypePlugins={[rehypeKatex]}
              children={post?.body || ""}
              components={{
                h1: MarkdownHeading1,
                h2: MarkdownHeading2,
                h3: MarkdownHeading3,
                a: MarkdownA,
                p: ({ node, className, children, ...props }) => (
                  <ErrorBoundary name={"MarkdownPage:ShowMessage:Paragraph-" + postId}
                                 key={"markdown-show-message-paragraph-" + postId}>
                    <p className={`my-4 ${className || ''}`} {...props}>
                      {children}
                    </p>
                  </ErrorBoundary>
                ),
                blockquote: ({ node, className, children, ...props }) => (
                  <ErrorBoundary name={"MarkdownPage:ShowMessage:Blockquote-" + postId}
                                 key={"markdown-show-message-blockquote-" + postId}>
                    <blockquote className={`border-l-4 border-gray-300 pl-4 italic ${className || ''}`} {...props}>
                      {children}
                    </blockquote>
                  </ErrorBoundary>
                ),
                ul: ({ node, className, children, ...props }) => (
                  <ErrorBoundary name={"MarkdownPage:ShowMessage:Ul-" + postId}
                                 key={"markdown-show-message-ul-" + postId}>
                    <ul className={`list-decimal pl-6 my-4 ${className || ''}`} {...props}>
                      {children}
                    </ul>
                  </ErrorBoundary>
                ),
                code(props) {
                  const { children, className, node, ref, ...rest } = props;
                  return (
                    <ErrorBoundary name={"MarkdownPage:ShowMessage:CodeBlock-" + postId}
                                   key={"markdown-show-message-codeblock-" + postId}>
                      <SyntaxHighlighter
                        {...rest}
                        PreTag="pre"
                        style={dark}
                      >
                        {String(children).replace(/\n$/, '')}
                      </SyntaxHighlighter>
                    </ErrorBoundary>
                  );
                }
              }}
            />
          </ErrorBoundary>
        </div>
      </div>
    </div>
  </ErrorBoundary>
);

export default function MarkdownPage({ children, group, isEditable, message, ...props }: MarkdownProps) {
  const navigate = useNavigate();

  const { roles, token } = useProfile();
  const [toggleEdit, setToggleEdit] = useState<boolean>(false);
  const [posts, setPosts] = useState<PostsById>({});
  const [postId, setPostId] = useState<string>("");
  const [edit, setEdit] = useState<Post>({ id: "", title: "", date: "", body: "", enable: false });
  const [impersonateMessage, setImpersonateMessage] = useState<string>("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isAdmin, setIsAdmin] = useState<Boolean>(false);

  useEffect(() => {
    if (roles && roles.includes("ADMIN")) {
      setIsAdmin(true);
    }
  }, [roles]);

  useEffect(() => {
    refreshPosts();
  }, []);

  useEffect(() => {
    if (!toggleEdit) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [toggleEdit]);

  const refreshPosts = () => {
    try {
      fetch(API.MARKDOWN(String(group), String(message)), {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(res => res.json())
        .then((res) => {
          setPosts(res[group] || {})
        }).catch((err) => {
        setError(`Помилка завантаження повідомлень: ${err.message}`);
        setSuccess("");
      });
    } catch (e) {
      setError(`Помилка завантаження повідомлень: ${e}`);
    }
  };

  useEffect(() => {
    if (!success && !error) {
      return;
    }
    setTimeout(() => {
      setSuccess("");
      setError("");
    }, 10000);
  }, [success, error]);

  const handleEditChange = (field: string, value: any): void => {
    setEdit((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updatePost = (edit: Post, postId: string, token: string | null, setError: (value: (((prevState: string) => string) | string)) => void, setSuccess: (value: (((prevState: string) => string) | string)) => void) => {
    // Save changes
    const method = postId === "___" ? "POST" : "PATCH";
    const url = API.MARKDOWN_POST(String(group), String(message), String(postId === "___" ? "" : postId));
    fetch(url, {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(edit)
    })
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((res) => {
        setSuccess("Зміни збережено успішно.");
        setError("");

        // Refresh posts
        refreshPosts();
        setToggleEdit(false);
      })
      .catch((err) => {
        setError(`Помилка збереження: ${err.message}`);
        setSuccess("");
      });

  };

  const removePost = (postId: string, token: string | null, setError: (value: (((prevState: string) => string) | string)) => void, setSuccess: (value: (((prevState: string) => string) | string)) => void) => {
    if (!window.confirm("Ви впевнені, що хочете видалити це повідомлення?")) {
      return;
    }

    try {
      // Delete post
      const url = API.MARKDOWN_POST(String(group), String(message), String(postId));
      fetch(url, {
        method: "DELETE",
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }).then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      }).then((res) => {
        setSuccess("Повідомлення видалено успішно.");
        setError("");

        // Refresh posts
        refreshPosts()
      }).catch((err) => {
        setError(`Помилка видалення: ${err.message}`);
        setSuccess("");
      });
    } catch (e) {
      setError(`Помилка видалення: ${e}`);
      setSuccess("");
    }
  }

  return (
    <Menu>
      <ErrorBoundary name={"MarkdownPage"}>
        <div className="">
          <Impersonate message={impersonateMessage} callbackExit={() => {
            try {
              localStorage.removeItem("impersonate");
            } catch (e) {
              console.error("Error removing impersonate from localStorage:", e);
            }
            setImpersonateMessage("");
            navigate("/users");
          }}/>

          {(success || error) &&
            <ErrorBoundary name={"MarkdownPage:MessageCard"}>
              <Card className="w-full max-w-xl mb-2 rounded-2xl shadow-lg flex justify-between">
                <CardContent className={"w-full flex flex-row justify-between"}>
                  <div className={"w-full flex flex-row justify-between"}>
                    {error && <div className="text-red-500 text-sm text-center">{error}</div>}
                    {success && <div className="text-green-600">{success}</div>}
                    <div className={"flex items-center mt-2 text-sm text-gray-500"}>
                      <FontAwesomeIcon icon={faClose} className="cursor-pointer ml-4" onClick={() => {
                        setError("");
                        setSuccess("");
                      }}/>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </ErrorBoundary>
          }

          {((isEditable && isAdmin) && !toggleEdit) && (
            <ErrorBoundary name={"MarkdownPage:AddNewPostCard"}>
              <Card className="w-full max-w-xl mb-2 rounded-2xl shadow-lg flex justify-between">
                <CardContent className={"w-full flex justify-between"}>
                  <Button onClick={() => {
                    setPostId("___");
                    setEdit({ id: "___", title: "", date: "", body: "", enable: true });
                    setToggleEdit(!toggleEdit);
                  }}><span>Додати новий запис</span></Button>
                </CardContent>
              </Card>
            </ErrorBoundary>
          )}
          {toggleEdit && (
            <ErrorBoundary name={"MarkdownPage:EditPostCard"}>
              <Card className="w-full max-w-xl mb-6 rounded-2xl shadow-lg flex justify-between">
                <CardContent className={"w-full flex justify-between"}>
                  {isAdmin && (
                    <div className={"w-full flex justify-between"}>
                      <form className="w-full">
                        <div className="flex flex-col w-full">
                          <div className="w-full">
                            <label className="text-sm font-medium">Назва:
                              <input
                                className="w-full px-3 py-2 rounded mt-1 border-2"
                                value={edit?.title || ""}
                                onChange={(e) => handleEditChange("title", e.target.value)}
                              /></label>
                            <label className="text-sm font-medium">Дата:
                              <input
                                className="w-full px-3 py-2 rounded mt-1 border-2"
                                value={edit?.date || ""}
                                onChange={(e) => handleEditChange("date", e.target.value)}
                              /></label>
                            <label className="text-sm font-medium">Повідомлення:
                              <textarea
                                rows={10}
                                className="w-full px-3 py-2 rounded mt-1 border-2"
                                value={edit?.body || ""}
                                onChange={(e) => handleEditChange("body", e.target.value)}
                              /></label>
                            <label className="text-sm font-medium mr-2">Відображати:
                              <input
                                type="checkbox"
                                className="px-3 py-2 rounded mt-1 border-2 ml-2"
                                checked={edit?.enable ?? true}
                                onChange={(e) => handleEditChange("enable", e.target.checked)}
                              /></label>
                          </div>

                          <div className="w-full flex items-center justify-between mt-1">
                            <Button type="button" onClick={() => {
                              if (postId !== "___") {
                                removePost(postId, token, setError, setSuccess);
                              }
                              setEdit({ id: "___", title: "", date: "", body: "", enable: false });
                              setToggleEdit(!toggleEdit);
                            }} className={"bg-red-600 hover:bg-red-700"}>
                              <FontAwesomeIcon icon={faTrashCan}/>
                            </Button>

                            <Button type="button" onClick={() => {
                              setEdit({ id: "___", title: "", date: "", body: "", enable: false });
                              setToggleEdit(!toggleEdit);
                            }} className={"bg-gray-600 hover:bg-gray-700"}>
                              <span>Скасувати</span>
                            </Button>
                            <Button type="button" onClick={() => updatePost(edit, postId, token, setError, setSuccess)}
                                    className={"bg-green-600 hover:bg-green-700"}>
                              <FontAwesomeIcon icon={faFloppyDisk}/>
                            </Button>
                          </div>
                        </div>
                      </form>
                    </div>
                  )}
                </CardContent>
              </Card>
            </ErrorBoundary>
          )}
          {Object.entries(posts).reverse().map(([id, post]: [string, Post], index) => {
              const postEnabled = post && (!isAdmin ? (post?.enable !== false) : true);
              if (!isEditable && post?.enable === false) {
                return (<></>);
              }
              return postEnabled ? (
                <ErrorBoundary name={"MarkdownPage:PostCard-" + id} key={`markdown-post-card-${id}-${index}`}>
                  <Card className="w-full max-w-xl mb-6 rounded-2xl shadow-lg">
                    <CardContent>
                      {(isEditable && isAdmin) && (
                        <div
                          className="flex justify-end space-x-2 mt-4 border-gray-200 border-b-2  p-2 bg-gradient-to-r from-white to-gray-100 rounded-t-lg">
                          <Button onClick={() => {
                            setPostId(id);
                            setEdit(post);
                            setToggleEdit(!toggleEdit);
                          }} className={""}><FontAwesomeIcon icon={faPencil} className=""/></Button>
                          <Button onClick={() => removePost(id, token, setError, setSuccess)}
                                  className={"bg-red-600 hover:bg-red-700"}>
                            <FontAwesomeIcon icon={faTrashCan} className=""/>
                          </Button>
                        </div>
                      )}
                      {ShowMessage(id, post)}
                      {(isEditable && isAdmin) && (
                        <div
                          className="flex justify-end space-x-2 mt-4 border-gray-200 border-t-2 p-2 bg-gradient-to-r from-white to-gray-100 rounded-b-lg">
                          <Button onClick={() => {
                            setPostId(id);
                            setEdit(post);
                            setToggleEdit(!toggleEdit);
                          }} className={""}><FontAwesomeIcon icon={faPencil} className=""/></Button>
                          <Button onClick={() => removePost(id, token, setError, setSuccess)}
                                  className={"bg-red-600 hover:bg-red-700"}>
                            <FontAwesomeIcon icon={faTrashCan} className=""/>
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </ErrorBoundary>
              ) : (<></>)
            }
          )}
        </div>
      </ErrorBoundary>
    </Menu>
  );
}
